import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Paper, TextField, Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete } from '@mui/icons-material';
import gitApi from './api';

const TokensPanel = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', read: true, write: true, expiresInDays: '' });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await gitApi.listTokens();
    setTokens(res.data?.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    const scopes = [form.read && 'read', form.write && 'write'].filter(Boolean);
    if (scopes.length === 0) { setError('Select at least one scope'); return; }
    const expiresInDays = form.expiresInDays ? parseInt(form.expiresInDays, 10) : null;
    setBusy(true);
    const res = await gitApi.createToken({ name: form.name.trim(), scopes, expiresInDays });
    setBusy(false);
    if (res.status === 200) {
      setCreated(res.data.token);
      setForm({ name: '', read: true, write: true, expiresInDays: '' });
      load();
    } else {
      setError(res.data?.message || 'Failed to create token');
    }
  };

  const revoke = async (id) => { await gitApi.revokeToken(id); load(); };

  return (
    <Paper sx={{ backgroundColor: '#1d1d1d', p: 2.5 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>Personal Access Tokens</Typography>
      <Typography sx={{ fontSize: 13, opacity: 0.7, mb: 2 }}>
        Use a token as the password for git clone/push over HTTPS and for git LFS.
      </Typography>

      <Box sx={{ backgroundColor: '#101010', p: 1.5, borderRadius: 1, mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>How to set up authentication</Typography>
        <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>
          1. Create a token above (enable <b>read</b>; add <b>write</b> if you need to push).
        </Typography>
        <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>
          2. When git prompts for credentials, enter your <b>IFMW username</b> and paste the token as the <b>password</b>.
          You can also embed it: <Box component="code" sx={{ fontFamily: 'monospace' }}>https://&lt;username&gt;:&lt;token&gt;@&lt;host&gt;/git/&lt;owner&gt;/&lt;repo&gt;.git</Box>
        </Typography>
        <Typography sx={{ fontSize: 12.5, opacity: 0.85, mt: 0.5 }}>
          3. Configure your client once — this server stores delta-free packs:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Box sx={{ fontFamily: 'monospace', fontSize: 12, backgroundColor: '#000', p: 1, borderRadius: 1, flex: 1, whiteSpace: 'pre-wrap' }}>
            {'git config --global push.thin false\ngit config --global pack.depth 0\ngit lfs install'}
          </Box>
          <IconButton size="small" title="Copy commands"
            onClick={() => navigator.clipboard?.writeText('git config --global push.thin false\ngit config --global pack.depth 0\ngit lfs install')}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: 12, opacity: 0.6, mt: 0.5 }}>
          Tokens are shown only once and can be revoked here at any time. Treat them like passwords.
        </Typography>
      </Box>

      {loading ? <CircularProgress /> : tokens.map((t) => (
        <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Typography sx={{ fontSize: 14 }}>{t.name}</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.6 }}>{t.prefix}…</Typography>
          {t.scopes.map((s) => <Chip key={s} size="small" label={s} />)}
          {t.expires && <Chip size="small" label={`expires ${new Date(t.expires).toLocaleDateString()}`} />}
          {t.lastUsed && <Typography sx={{ fontSize: 11, opacity: 0.5 }}>last used {new Date(t.lastUsed).toLocaleString()}</Typography>}
          <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => revoke(t.id)}><Delete fontSize="small" /></IconButton>
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" label="Token name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <FormControlLabel control={<Checkbox checked={form.read} onChange={(e) => setForm({ ...form, read: e.target.checked })} />} label="read" />
        <FormControlLabel control={<Checkbox checked={form.write} onChange={(e) => setForm({ ...form, write: e.target.checked })} />} label="write" />
        <TextField size="small" label="Expires (days)" type="number" value={form.expiresInDays} onChange={(e) => setForm({ ...form, expiresInDays: e.target.value })} sx={{ width: 140 }} />
        <Button variant="outlined" startIcon={<Add />} onClick={create} disabled={busy}>Create token</Button>
      </Box>
      {error && <Typography color="error" sx={{ mt: 1, fontSize: 13 }}>{error}</Typography>}

      <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>Token created</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1 }}>Copy this token now — it will not be shown again.</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField fullWidth value={created || ''} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
            <IconButton onClick={() => navigator.clipboard?.writeText(created)}><ContentCopy /></IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreated(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TokensPanel;
